import { useState } from "react";
import type React from "react";
import { theme, card, countTag, type as text } from "../theme";
import { fmtDate } from "../lib/format";

// The improvement register as a board: one column per action status. A card is a summary only:
// clicking it opens the action's read-only view, where the status moves sit under Actions (which
// is also the keyboard and screen-reader route). People who may edit can still drag a card to
// another column, and only to the statuses the server's state machine allows from where it is:
// the transitions come from GET /improvement/catalogue, never a copy kept here. The move itself
// is the page's: it calls the same PUT the form does, so every server rule (root cause before
// completion, verification only from completed) still applies.

export interface BoardAction {
  id: string;
  displayId: number | null;
  title: string;
  status: string;
  ownerName: string | null;
  dueDate: string | null;
}

export function ImprovementBoard<T extends BoardAction>({
  actions, statuses, transitions, onOpen, onMove, badge, busyId, canMove = true,
}: {
  actions: T[];
  statuses: Array<{ id: string; label: string }>;
  transitions: Record<string, readonly string[]>;
  onOpen: (a: T) => void;
  onMove: (a: T, to: string) => void;
  /** Health badge or anything else the page wants on the card. */
  badge?: (a: T) => React.ReactNode;
  /** The card currently being saved, shown dimmed. */
  busyId?: string | null;
  /** Whether cards can be dragged between columns (the viewer may edit). */
  canMove?: boolean;
}) {
  const [dragging, setDragging] = useState<T | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const allowed = (a: T | null, to: string) => !!a && a.status !== to && (transitions[a.status] ?? []).includes(to);

  return (
    <div style={{ overflowX: "auto", paddingBottom: 6 }}>
      <div role="list" aria-label="Actions by status" style={{ display: "grid", gridTemplateColumns: `repeat(${statuses.length}, minmax(210px, 1fr))`, gap: 10, minWidth: statuses.length * 220 }}>
        {statuses.map((col) => {
          const cards = actions.filter((a) => a.status === col.id);
          const target = dragging ? allowed(dragging, col.id) : false;
          return (
            <section
              key={col.id}
              aria-label={`${col.label}, ${cards.length} action${cards.length === 1 ? "" : "s"}`}
              onDragOver={(e) => { if (target) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOver(col.id); } }}
              onDragLeave={() => setOver((o) => (o === col.id ? null : o))}
              onDrop={(e) => {
                e.preventDefault();
                const a = dragging;
                setDragging(null); setOver(null);
                if (a && allowed(a, col.id)) onMove(a, col.id);
              }}
              style={{
                background: theme.color.panelAlt, borderRadius: theme.radius.md, padding: 8, minHeight: 120,
                border: `${over === col.id ? 2 : 1}px ${target ? "dashed" : "solid"} ${over === col.id ? theme.color.accent : target ? theme.color.accent : theme.color.border}`,
                opacity: dragging && !target && dragging.status !== col.id ? 0.55 : 1,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 4px 8px" }}>
                <span style={{ ...text.label, marginBottom: 0, color: theme.color.heading }}>{col.label}</span>
                <span style={countTag}>{cards.length}</span>
              </div>
              <div style={{ display: "grid", gap: 8 }}>
                {cards.map((a) => {
                  const moves = canMove ? transitions[a.status] ?? [] : [];
                  return (
                    <div
                      key={a.id}
                      role="listitem"
                      draggable={moves.length > 0}
                      onDragStart={(e) => { setDragging(a); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", a.id); }}
                      onDragEnd={() => { setDragging(null); setOver(null); }}
                      style={{ ...card, padding: 10, cursor: moves.length ? "grab" : "default", opacity: busyId === a.id ? 0.5 : 1 }}
                    >
                      <button
                        type="button"
                        onClick={() => onOpen(a)}
                        style={{ display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontFamily: theme.font }}
                      >
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                          {badge?.(a)}
                          {a.displayId !== null && <span style={{ ...text.ref, fontSize: text.meta.fontSize, color: theme.color.faint }}>CA-{a.displayId}</span>}
                        </div>
                        <div style={{ ...text.itemTitle, fontSize: text.hint.fontSize, lineHeight: 1.35 }}>{a.title}</div>
                        <div style={{ ...text.meta, color: theme.color.subtle, marginTop: 4 }}>
                          {a.ownerName ?? "No owner"}{a.dueDate && <> · due {fmtDate(a.dueDate)}</>}
                        </div>
                      </button>
                    </div>
                  );
                })}
                {cards.length === 0 && <div style={{ ...text.meta, padding: "6px 4px" }}>No actions</div>}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
